import React from 'react';
import { Loader2, Undo2, Redo2, RefreshCw } from 'lucide-react';
import { processDiffedText } from './utils';

interface ResumeFeedbackPanelProps {
  json: any;
  history: any[];
  historyIndex: number;
  handleUndo: () => void;
  handleRedo: () => void;
  feedback: string;
  setFeedback: (val: string) => void;
  handleGenerate: (txt: string) => void;
  handleCancel?: () => void;
  isGenerating: boolean;
  resumeData: any;
  error: string;
}

export const ResumeFeedbackPanel: React.FC<ResumeFeedbackPanelProps> = ({
  json,
  history,
  historyIndex,
  handleUndo,
  handleRedo,
  feedback,
  setFeedback,
  handleGenerate,
  handleCancel,
  isGenerating,
  resumeData,
  error
}) => {
  return (
    <div className="w-full md:w-[320px] flex flex-col border-t md:border-t-0 md:border-l border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden shrink-0 h-[350px] md:h-full rounded-br-xl rounded-bl-xl md:rounded-bl-none z-30">
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <div className="space-y-2 shrink-0">
          <div className="bg-amber-50 dark:bg-amber-900/20 p-2.5 rounded-lg border border-amber-200 dark:border-amber-800/30">
            <p className="text-xs text-amber-800 dark:text-amber-400 leading-relaxed font-medium">
              This tool utilizes a standardized, ATS-friendly PDF template. Your original visual layout (columns, colors) cannot be preserved. Use this panel to tweak the <strong>content</strong> of the tailored resume.
            </p>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/40 p-2.5 rounded-lg border border-blue-200 dark:border-blue-700/50">
            <p className="text-xs text-blue-800 dark:text-blue-200 leading-relaxed font-medium">
              Company hyperlinks might be removed to ensure a clean, standard ATS-friendly format.
            </p>
          </div>
        </div>

        {json?.redFlags && json.redFlags.length > 0 && (
          <div className="bg-rose-50 dark:bg-rose-900/10 border border-rose-200 dark:border-rose-800/30 p-2.5 rounded-lg shrink-0 space-y-2">
            <h4 className="text-sm font-semibold text-rose-800 dark:text-rose-400">Potential Red Flags</h4>
            <ul className="list-disc list-outside ml-3.5 space-y-1">
              {json.redFlags.map((flag: string, idx: number) => (
                <li key={idx} className="text-xs text-rose-700 dark:text-rose-300 leading-relaxed pl-0.5">
                  {processDiffedText(flag)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {json?.suggestions && json.suggestions.length > 0 && (
          <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-2.5 rounded-lg shrink-0 space-y-2">
            <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200">Resume Suggestions</h4>
            <ul className="list-disc list-outside ml-3.5 space-y-1">
              {json.suggestions.map((suggestion: string, idx: number) => (
                <li key={idx} className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed pl-0.5">
                  {processDiffedText(suggestion)}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-col space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">Adjustments</h3>
            <div className="flex items-center space-x-1.5">
              <button 
                onClick={handleUndo} 
                disabled={historyIndex <= 0 || isGenerating}
                className="p-1 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                title="Undo"
              >
                <Undo2 className="w-4 h-4" />
              </button>
              <button 
                onClick={handleRedo} 
                disabled={historyIndex >= history.length - 1 || isGenerating}
                className="p-1 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                title="Redo"
              >
                <Redo2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="e.g., 'Make it 1 page', 'Keep my Github and company URLs'"
            className="w-full p-2.5 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[80px] resize-y"
          />
          
          {isGenerating ? (
            <button
              onClick={handleCancel}
              className="w-full flex items-center justify-center gap-1.5 py-1.5 px-3 bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800 text-sm font-medium rounded-lg transition-colors cursor-pointer"
            >
              <Loader2 className="w-4 h-4 animate-spin" />
              Cancel Generation
            </button>
          ) : (
            <button
              onClick={() => handleGenerate(feedback)}
              disabled={(!resumeData && !feedback.trim())}
              className="w-full flex items-center justify-center gap-1.5 py-1.5 px-3 bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 cursor-pointer"
            >
              <RefreshCw className="w-4 h-4" />
              Regenerate
            </button>
          )}
          {error && (
            <p className="text-xs text-rose-500 text-center font-medium bg-rose-50 dark:bg-rose-900/20 p-2 rounded">
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
